package com.routexk.quiz.analytics

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLScriptElement

/**
 * GA4 tracking per RouteXK_QuizFunnel_GA4_Event_Spec.xlsx (repo root).
 *
 * The funnel is a client-side SPA (7 quiz questions + the analysis screen all
 * live under /quiz), so Enhanced Measurement page views are disabled
 * (send_page_view: false) and every step fires screen_view manually.
 *
 * Every call is a silent no-op until [MEASUREMENT_ID] is filled in, so the
 * site works unchanged without a GA4 property.
 */
object Ga4 {

    /** GA4 web stream Measurement ID (GA4 Admin → Data streams). */
    private const val MEASUREMENT_ID = "G-REEDT03B3R"

    /**
     * Value attached to generate_lead (spec: "[agreed lead value]"). Adjust
     * once Growth agrees on the number fed to Google Ads Smart Bidding.
     */
    const val LEAD_VALUE_USD = 1.0

    /** Coupon label the spec attaches to begin_checkout / purchase. */
    const val COUPON = "DISCOUNT60"

    private var initialized = false

    /** Injects gtag.js once per page load. Safe to call repeatedly. */
    fun init() {
        if (initialized || MEASUREMENT_ID.isBlank()) return
        initialized = true
        js("window.dataLayer = window.dataLayer || []")
        js("window.gtag = function() { window.dataLayer.push(arguments); }")
        window.asDynamic().gtag("js", js("new Date()"))
        val config = js("{}")
        config.send_page_view = false
        window.asDynamic().gtag("config", MEASUREMENT_ID, config)
        val script = document.createElement("script") as HTMLScriptElement
        script.async = true
        script.src = "https://www.googletagmanager.com/gtag/js?id=$MEASUREMENT_ID"
        document.head?.appendChild(script)
    }

    /** Fires a GA4 event; null-valued params are dropped. */
    fun event(name: String, vararg params: Pair<String, Any?>) {
        if (!initialized) return
        val obj = js("{}")
        params.forEach { (key, value) -> if (value != null) obj[key] = value }
        window.asDynamic().gtag("event", name, obj)
    }

    fun screenView(screenName: String, funnelStep: Int, vararg extra: Pair<String, Any?>) {
        event("screen_view", "screen_name" to screenName, "funnel_step" to funnelStep, *extra)
    }

    /**
     * Reads the GA client_id and session_id (async — gtag answers once the
     * library loads). Calls back with nulls when GA4 is disabled or blocked.
     * The funnel stores these in the backend session so the server-side
     * purchase (Measurement Protocol) lands on the same GA4 user/session.
     */
    fun withClientIds(callback: (clientId: String?, sessionId: String?) -> Unit) {
        if (!initialized) {
            callback(null, null)
            return
        }
        val gtag = window.asDynamic().gtag
        gtag("get", MEASUREMENT_ID, "client_id") { clientId: dynamic ->
            gtag("get", MEASUREMENT_ID, "session_id") { sessionId: dynamic ->
                callback(clientId as? String, sessionId?.toString())
            }
        }
    }

    /** User-scoped property, e.g. user_city from quiz Q1. */
    fun setUserProperty(name: String, value: String) {
        if (!initialized) return
        val props = js("{}")
        props[name] = value
        window.asDynamic().gtag("set", "user_properties", props)
    }

    // --- Funnel-specific helpers -------------------------------------------

    /** Spec plan_id by paid-trial length: 14d → trial_2w, 30d → plan_1m, 365d → plan_1y. */
    fun planId(trialDays: Int): String = when {
        trialDays <= 14 -> "trial_2w"
        trialDays <= 31 -> "plan_1m"
        else -> "plan_1y"
    }

    /** GA4 ecommerce item for a plan. */
    fun planItem(planId: String, label: String, amountCents: Long): dynamic {
        val item = js("{}")
        item.item_id = planId
        item.item_name = label
        item.price = amountCents / 100.0
        item.quantity = 1
        return item
    }

    fun itemsArray(items: List<dynamic>): dynamic {
        val arr = js("[]")
        items.forEach { arr.push(it) }
        return arr
    }

    /** Maps a Stripe error message onto the spec's failure_reason buckets. */
    fun failureReason(message: String): String {
        val m = message.lowercase()
        return when {
            "declined" in m -> "declined"
            "insufficient" in m -> "insufficient_funds"
            "network" in m || "connection" in m || "timeout" in m -> "network_error"
            else -> "other"
        }
    }
}
